import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Header from '../../components/ui/Header';
import ReviewHeader from './components/ReviewHeader';
import ExpertRating from './components/ExpertRating';
import ProsAndCons from './components/ProsAndCons';
import VideoReview from './components/VideoReview';
import UserReviews from './components/UserReviews';
import ProductReviewContent from './components/ProductReviewContent';
import AffiliateLinks from './components/AffiliateLinks';
import productService from '../../services/productService';

export default function ProductReview() {
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadProductData();
  }, [productId]);

  // Real-time subscription for reviews
  useEffect(() => {
    if (!product?.id) return;

    const unsubscribe = productService?.subscribeToReviews(
      product?.id,
      (payload) => {
        console.log('Review update:', payload);
        // Reload product data to get updated reviews
        loadProductData();
      }
    );

    return () => unsubscribe();
  }, [product?.id]);

  const loadProductData = async () => {
    try {
      setLoading(true);
      setError('');

      // Load product with all related data
      const productData = await productService?.getProductBySlug(productId);
      
      if (!productData) {
        setError('Product not found');
        setLoading(false);
        return;
      }

      setProduct(productData);

      // Load related products
      const related = await productService?.getRelatedProducts(
        productData?.id,
        productData?.category,
        3
      );
      setRelatedProducts(related);

    } catch (err) {
      console.error('Error loading product:', err);
      setError(err?.message || 'Failed to load product');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToComparison = () => {
    console.log('Added to comparison:', product?.name);
  };

  const handleShareReview = () => {
    if (navigator.share) {
      navigator.share({
        title: `${product?.name} Review - Vacuum Guide`,
        text: `Check out this detailed review of the ${product?.name}`,
        url: window.location?.href
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
        {loading ? (
          <div className="animate-pulse space-y-8">
            <div className="bg-white rounded-xl p-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="aspect-square bg-muted rounded-lg" />
                <div className="space-y-4">
                  <div className="h-8 bg-muted rounded w-3/4" />
                  <div className="h-4 bg-muted rounded w-full" />
                  <div className="h-4 bg-muted rounded w-2/3" />
                  <div className="h-12 bg-muted rounded w-1/2" />
                </div>
              </div>
            </div>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
            <p className="text-red-600 font-medium">{error}</p>
          </div>
        ) : product ? (
          <div className="space-y-8">
            <ReviewHeader
              product={product}
              onAddToComparison={handleAddToComparison}
              onShareReview={handleShareReview}
            />

            {product?.productReview && (
              <ProductReviewContent review={product?.productReview} />
            )}

            <ExpertRating ratings={product?.expertRatings || []} />

            {product?.videoReview && (
              <VideoReview video={product?.videoReview} />
            )}

            <ProsAndCons pros={product?.pros || []} cons={product?.cons || []} />

            {product?.affiliateLinks && product?.affiliateLinks?.length > 0 && (
              <AffiliateLinks 
                affiliateLinks={product?.affiliateLinks} 
                productName={product?.name}
              />
            )}

            <UserReviews
              reviews={product?.reviews || []}
              averageRating={product?.rating || 0}
              totalReviews={product?.reviewCount || 0}
            />
          </div>
        ) : null}
      </main>
    </div>
  );
}